// src/utils/mediaGroupingUtil.ts
import { Message } from '../../../store/slices/messagesSlice';
import { Logger } from '../../../utils/logger';

/**
 * Maximum time difference (in minutes) between messages to be considered part of the same group
 */
const MAX_GROUP_TIME_DIFF = 5;

/**
 * Minimum group size to trigger grouping display
 */
const MIN_GROUP_SIZE = 2;

/**
 * Maximum number of media items to show in grid before showing "+X more"
 */
const MAX_VISIBLE_ITEMS = 6;

/**
 * Configuration for different grid layouts based on group size
 */
interface GridConfig {
  columns: number;
  rows: number;
  template: string;
}

const GRID_CONFIGS: { [key: number]: GridConfig } = {
  2: { columns: 2, rows: 1, template: '1fr 1fr' },
  3: { columns: 3, rows: 1, template: '1fr 1fr 1fr' },
  4: { columns: 2, rows: 2, template: '1fr 1fr / 1fr 1fr' },
  5: { columns: 3, rows: 2, template: '1fr 1fr 1fr / 1fr 1fr' },
  6: { columns: 3, rows: 2, template: '1fr 1fr 1fr / 1fr 1fr 1fr' },
};

/**
 * Checks if a message contains media
 */
export const isMediaMessage = (message: Message): boolean => {
  return Boolean(
    message && 
    message.messageType && 
    ['image', 'video', 'audio', 'document'].includes(message.messageType)
  );
};

/**
 * Returns true if media can be grouped (currently only images and videos)
 */
export const isGroupableMedia = (message: Message): boolean => {
  return Boolean(
    message && 
    message.messageType && 
    ['image', 'video'].includes(message.messageType)
  );
};

/**
 * Check if two messages should be in the same group based on sender, time, and media type
 */
export const shouldBeGrouped = (msg1: Message, msg2: Message): boolean => {
  if (!isGroupableMedia(msg1) || !isGroupableMedia(msg2)) {
    return false;
  }

  // Same sender
  if (msg1.sender !== msg2.sender) {
    return false;
  }

  // Time proximity check
  const time1 = new Date(msg1.createdAt).getTime();
  const time2 = new Date(msg2.createdAt).getTime();
  const diffMinutes = Math.abs(time1 - time2) / (1000 * 60);

  return diffMinutes <= MAX_GROUP_TIME_DIFF;
};

/**
 * Find all media groups in a list of messages
 * Returns an array of arrays, where each inner array is a group of media messages
 */
export const findMediaGroups = (messages: Message[]): Message[][] => {
  const groups: Message[][] = [];
  let currentGroup: Message[] = [];

  messages.forEach((message, index) => {
    if (isGroupableMedia(message)) {
      // Check if we should start a new group
      if (
        currentGroup.length === 0 || 
        shouldBeGrouped(currentGroup[currentGroup.length - 1], message)
      ) {
        currentGroup.push(message);
      } else {
        // This message starts a new group
        if (currentGroup.length >= MIN_GROUP_SIZE) {
          groups.push([...currentGroup]);
        }
        currentGroup = [message];
      }
    } else {
      // Not a groupable media message
      if (currentGroup.length >= MIN_GROUP_SIZE) {
        groups.push([...currentGroup]);
      }
      currentGroup = [];
    }
  });

  // Handle the last group
  if (currentGroup.length >= MIN_GROUP_SIZE) {
    groups.push([...currentGroup]);
  }

  return groups;
};

/**
 * Generate a unique ID for a media group
 */
export const getMediaGroupId = (messages: Message[]): string => {
  if (!messages.length) return '';
  
  // Use first and last message ID and count
  const firstId = messages[0]?._id || '';
  const lastId = messages[messages.length - 1]?._id || '';
  return `group_${firstId}_${lastId}_${messages.length}`;
};

/**
 * Get grid configuration based on group size
 */
export const getGridConfig = (groupSize: number): GridConfig => {
  // Handle up to 6 items, then fallback to 6-item grid
  const size = Math.min(groupSize, 6);
  return GRID_CONFIGS[size] || GRID_CONFIGS[6];
};

/**
 * Calculate dimensions for each media item in a grid
 */
export const getMediaItemStyle = (groupSize: number, index: number): { [key: string]: string } => {
  if (groupSize <= 1) {
    return {}; // Single item, no special styling
  }

  if (groupSize === 2) {
    return {
      width: '100%',
      height: '140px',
      display: 'flex',
      flexDirection: 'column',
    };
  }

  if (groupSize === 3) {
    return {
      width: '100%',
      height: '140px',
      display: 'flex',
      flexDirection: 'column',
    };
  }

  // 4 or more items
  return {
    width: '100%',
    height: '140px',
    display: 'flex',
    flexDirection: 'column',
  };
};

/**
 * Find all media items in a conversation for navigation
 */
export const getAllMediaInConversation = (messages: Message[]): Message[] => {
  return messages.filter(msg => isGroupableMedia(msg));
};

/**
 * Find the next/previous media item for navigation
 */
export const findAdjacentMedia = (
  messages: Message[], 
  currentId: string, 
  direction: 'next' | 'prev'
): Message | null => {
  const mediaMessages = getAllMediaInConversation(messages);
  const currentIndex = mediaMessages.findIndex(msg => msg._id === currentId);
  
  if (currentIndex === -1) return null;
  
  const adjacentIndex = direction === 'next' 
    ? currentIndex + 1 
    : currentIndex - 1;
    
  if (adjacentIndex < 0 || adjacentIndex >= mediaMessages.length) {
    return null;
  }
  
  return mediaMessages[adjacentIndex];
};

/**
 * Get statistics for media navigation (current position, total)
 */
export const getMediaNavigationStats = (
  messages: Message[],
  currentId: string
): { current: number; total: number } => {
  const mediaMessages = getAllMediaInConversation(messages);
  const currentIndex = mediaMessages.findIndex(msg => msg._id === currentId);
  
  if (currentIndex === -1) {
    return { current: 0, total: mediaMessages.length };
  }
  
  return {
    current: currentIndex + 1,
    total: mediaMessages.length,
  };
};

/**
 * Determine if we should show "+X more" indicator
 */
export const shouldShowMoreIndicator = (groupSize: number): boolean => {
  return groupSize > MAX_VISIBLE_ITEMS;
};

/**
 * Get text for "+X more" indicator
 */
export const getMoreIndicatorText = (groupSize: number): string => {
  const extraCount = groupSize - MAX_VISIBLE_ITEMS;
  return `+${extraCount} more`;
};